// -----------------------------------------------------------------------------
// src/components/CreatorHeader.jsx – Updated to match BlitzHeader styling
// -----------------------------------------------------------------------------
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AppBar, Toolbar, IconButton, Box, useMediaQuery, Tooltip, Avatar } from '@mui/material';
import { useTheme, ThemeProvider } from '@mui/material/styles';
import blitzLogo from './globalAssets/logo_placeholder.png';
import routes from '../Config/routes';
import { lightTheme } from '../Utils/baseTheme';
import { useCreatorAuth } from "../Hooks/creator-use-auth";

/**
 * CreatorHeader
 * ─────────────
 * • Desktop → menu button LEFT | creator avatar | logo follows | rest of bar
 * • Mobile → creator avatar LEFT | logo | spacer | notification & settings icons RIGHT
 *
 * Updated to match BlitzHeader visual styling and layout
 */
const CreatorHeader = ({
  menuButton,
  children,
  isDesktop: isDesktopProp,
  mobileActions = []
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const upMd = useMediaQuery(theme.breakpoints.up('md'));
  const isDesktop = typeof isDesktopProp === 'boolean' ? isDesktopProp : upMd;

  const { creatorToken, isAuthorized: creatorOK } = useCreatorAuth();

  // Get creator avatar and display name (no random placeholders)
  let displayName = "Me";
  let photo; // undefined → blank avatar

  if (creatorOK && creatorToken?.creator_user) {
    const c = creatorToken.creator_user;
    displayName = c.creator?.display_name || c.username || displayName;
    photo = c.creator?.pfphref || c.pfphref || undefined;
  }

  return (
    <ThemeProvider theme={lightTheme}>
      <AppBar
        id='CreatorHeader'
        position="fixed"
      >
        <Toolbar>
          <Box
            display='flex'
            flexDirection='row'
            justifyContent='space-between'
            alignItems='center'
            flex={1}
            maxHeight='100%'
          >
            {isDesktop ? (
              <>
                {/* Desktop: Menu button first, then creator avatar, then logo */}
                {menuButton}
                {creatorOK && (
                  <Tooltip title={`Go to ${displayName}'s profile`} placement="bottom">
                    <IconButton
                      color="inherit"
                      onClick={() => navigate('/bio')}
                      sx={{ 
                        ml: 1,
                        "&:hover": { 
                          transform: "scale(1.05)",
                        }
                      }}
                    >
                      <Avatar
                        src={photo}
                        alt={displayName}
                        sx={{ 
                          width: 36, 
                          height: 36,
                          border: "2px solid",
                          borderColor: theme.palette.primary.main,
                          boxShadow: theme.shadows[2],
                          transition: 'transform 0.2s ease-in-out',
                          '&:hover': {
                            transform: 'scale(1.05)',
                          }
                        }}
                      />
                    </IconButton>
                  </Tooltip>
                )}
                <Box margin={0} flex={1}>
                  <IconButton
                    color="inherit"
                    aria-label="logo"
                    sx={{
                      margin: 0,
                      padding: 0,
                      ml: creatorOK ? 1 : 1,
                      "&.MuiButtonBase-root:hover": {
                        bgcolor: "transparent"
                      }
                    }}
                    onClick={() => navigate(routes.creatorConnectStart)}
                  >
                    <img
                      src={blitzLogo}
                      alt="logo"
                      style={{ height: "45px" }}
                    />
                  </IconButton>
                </Box>
                {children}
              </>
            ) : (
              <>
                {/* Mobile: Creator avatar first, then logo */}
                {creatorOK && (
                  <Tooltip title={`Go to ${displayName}'s profile`} placement="bottom">
                    <IconButton
                      edge="start"
                      color="inherit"
                      onClick={() => navigate('/bio')}
                      sx={{ 
                        mr: 1,
                        "&:hover": { 
                          transform: "scale(1.05)",
                        }
                      }}
                    >
                      <Avatar
                        src={photo}
                        alt={displayName}
                        sx={{ 
                          width: 36, 
                          height: 36,
                          border: "2px solid",
                          borderColor: theme.palette.primary.main,
                          boxShadow: theme.shadows[2],
                          transition: 'transform 0.2s ease-in-out',
                          '&:hover': {
                            transform: 'scale(1.05)',
                          }
                        }}
                      />
                    </IconButton>
                  </Tooltip>
                )}
                <Box margin={0} flex={1}>
                  <IconButton
                    color="inherit"
                    aria-label="logo"
                    sx={{
                      margin: 0,
                      padding: 0,
                      "&.MuiButtonBase-root:hover": {
                        bgcolor: "transparent"
                      }
                    }}
                    onClick={() => navigate(routes.creatorConnectStart)}
                  >
                    <img
                      src={blitzLogo}
                      alt="logo"
                      style={{ height: "45px" }}
                    />
                  </IconButton>
                </Box>
                {children}
                {/* Mobile: Action buttons instead of menu button */}
                {mobileActions.map((action) => (
                  <Tooltip key={action.key} title={action.tooltip} placement="bottom">
                    <IconButton
                      color="inherit"
                      onClick={action.onClick}
                      sx={{ ml: 1 }}
                      aria-label={action.tooltip}
                    >
                      {action.icon}
                    </IconButton>
                  </Tooltip>
                ))}
              </>
            )}
          </Box>
        </Toolbar>
      </AppBar>
    </ThemeProvider>
  );
};

export default CreatorHeader;
